import { useCallback, useRef, useEffect, useReducer, useLayoutEffect } from "react";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";
import { MDSMenuItemHeader, MDSSelectMenu, MDSSelectMenuItem } from "@/mds/components/menu";
import {
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
} from "@/mds/components/forms/text-field";
import { CommonEditorInput } from "./common-editor-input";
import {
  MDSComboboxProps,
  MDSComboboxSlotName,
  ComboboxState,
  MDSComboboxMention,
  MDSComboboxMode,
  MDSComboboxItem,
} from "./combobox.types";
import { comboboxReducer } from "./combobox.reducer";
import {
  StyledCombobox,
  StyledLabel,
  StyledInputContainer,
  StyledLeftContainer,
  StyledRightContainer,
  StyledInputArea,
  StyledErrorMessage,
  StyledExpand,
  StyledComboboxPopover,
  StyledPopoverContent,
  StyledEmptyResult,
  StyledLeftAddonContainer,
  StyledPopoverSearchContainer,
} from "./combobox.style";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import type { Selection } from "react-aria-components";
import { getSlot } from "@/mds/utils/slots";
import { JsonObject } from "type-fest";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MentionChips } from "./mention-chips";
import { MDSComboboxListItemIcon } from "./combobox-list-item-icon";
import { MDSSelectMenuItemProps } from "@/mds/components/menu/select-menu/select-menu.types";
import { getMenuItemSubtitle } from "@/mds/components/menu/helpers";

export const MDSCombobox = <T extends MDSComboboxItem>(props: MDSComboboxProps<T>) => {
  const {
    fetchData,
    label,
    placeholder,
    errorMessage,
    isDisabled = false,
    chatIsClosed = false,
    variant,
    children,
    popoverProps,
    autoFocus = false,
    onSubmit,
    onInputFocus,
    onInputBlur,
    onInputChange,
    inputRef: externalInputRef,
    mode = MDSComboboxMode.Default,
    editorDimensions,
    setContent,
    onContentModified,
    className,
    fetchIsInstant,
    mentions,
    onMentionAdd,
    onMentionRemove,
    onMentionsClear,
    implicitMentions = [],
    forceClose,
    onLeftAddonButtonClick,
  } = props;

  const [state, dispatch] = useReducer(comboboxReducer<T>, {
    isOpen: false,
    query: "",
    isEmpty: true,
    isLoading: false,
    data: [],
    hasSearched: false,
    highlightedItemId: null,
    mentions: [],
    popoverMentionMode: "implicit",
  } as ComboboxState<T>);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const inputRef = externalInputRef || internalInputRef;
  const isProgrammaticFocusRef = useRef<boolean>(false);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  // Track ongoing requests to handle race conditions
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set the content of the editor
  useLayoutEffect(() => {
    if (!setContent) return;

    // Wait for editor to be initialized.
    const h = setTimeout(() => {
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.SetEditorContent,
        payload: { content: setContent },
      });
    }, 10);

    return () => clearTimeout(h);
  }, [inputRef, setContent]);

  // Handle external force close
  useEffect(() => {
    if (forceClose && state.isOpen) {
      dispatch({ type: "POPOVER_CLOSED" });
    }
  }, [forceClose, state.isOpen]);

  const cleanupLoadingTimeout = () => {
    if (!loadingTimeoutRef.current) {
      return;
    }

    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = null;
  };

  // Emit input height to consumer on content changes
  const emitInputHeightChange = useCallback(() => {
    const containerEl = inputContainerRef.current;
    const measuredHeight = containerEl?.offsetHeight;
    if (measuredHeight && typeof measuredHeight === "number") {
      onInputChange?.(measuredHeight);
    } else {
      onInputChange?.();
    }
  }, [onInputChange]);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      // Only focus if clicking directly on the container or its immediate layout wrappers,
      // not if clicking on interactive children that bubbled up
      if (e.target !== e.currentTarget) {
        return;
      }

      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
    },
    [inputRef]
  );

  // Handle mention queries
  const handleMentionQuery = useCallback(
    async (popoverMentionMode: "explicit" | "implicit", queryStr?: string) => {
      dispatch({ type: "QUERY_CHANGED", payload: queryStr ?? "" });

      const trimmedQuery = queryStr?.trim() ?? "";

      // Check if query starts with mention symbols
      const startsWithMentionSymbol = ["@", "#"].includes(trimmedQuery.charAt(0));

      if (!startsWithMentionSymbol && popoverMentionMode === "implicit") {
        // Close popover and clear data if no mention symbol
        dispatch({ type: "RESET" });

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        return;
      }

      const mentionSymbol = trimmedQuery.charAt(0);
      let query = trimmedQuery;
      if (popoverMentionMode === "implicit") {
        query = trimmedQuery.slice(1); // Remove the @ or # symbol
      }

      // We want to load even if the query is empty
      if (trimmedQuery.length >= 0) {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Start new search - this resets state and closes popover
        dispatch({ type: "SEARCH_STARTED" });
        dispatch({ type: "POPOVER_MENTION_MODE_CHANGED", payload: popoverMentionMode });

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Only set loader if we expect long fecthing times
        if (!fetchIsInstant) {
          dispatch({ type: "LOADING_STARTED" });
        }

        try {
          const result = await fetchData({ query, mentionSymbol });

          // Only update if this request wasn't cancelled
          if (!abortController.signal.aborted) {
            dispatch({ type: "SEARCH_COMPLETED", payload: result.data });
            dispatch({ type: "LOADING_STOPPED" });
            cleanupLoadingTimeout();
          }
        } catch (_error) {
          if (abortController.signal.aborted) {
            return;
          }

          dispatch({ type: "SEARCH_FAILED" });
          dispatch({ type: "LOADING_STOPPED" });
          cleanupLoadingTimeout();
        }
      }
    },

    [fetchData, fetchIsInstant]
  );

  // Update popover width to match input container
  useLayoutEffect(() => {
    if (inputContainerRef.current && popoverContentRef.current) {
      const containerWidth = inputContainerRef.current.offsetWidth;
      popoverContentRef.current.style.setProperty(
        "--combobox-popover-trigger-width",
        `${containerWidth}px`
      );
    }
  }, [state.isOpen, state.data.length, state.hasSearched]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Handle editor empty state changes
  const handleIsEmptyChange = useCallback((empty: boolean) => {
    dispatch({ type: "EMPTY_STATE_CHANGED", payload: empty });
  }, []);

  const handleSelection = async (keys: Selection) => {
    const selected = keys as Set<string>;
    const uuid = selected.values().next().value;
    const item = state.data.find(item => item.id === uuid);

    if (!item) return;

    const label = (await item.onClick?.()) ?? item.label;

    const kind = (() => {
      if (item.kind === "create-new-collection") {
        return MemCommonMentionKind.Collection;
      }
      return item.kind ?? MemCommonMentionKind.Note;
    })();

    if (state.popoverMentionMode === "explicit") {
      // Plus button mode - add to mentions via handler
      let mention: MDSComboboxMention;

      if (kind === MemCommonMentionKind.Collection) {
        mention = {
          kind: MemCommonMentionKind.Collection,
          isExternal: false,
          collection_id: item.id,
          collection_title: label,
        };
      } else {
        mention = {
          kind: MemCommonMentionKind.Note,
          isExternal: false,
          note_id: item.id,
          note_primary_label: label,
        };
      }

      onMentionAdd?.(mention);
    } else {
      // Implicit mode - insert into input text
      inputRef.current?.dispatchAction?.({
        kind: MemCommonRichTextInputActionKind.CloseMention,
        payload: {
          id: item?.id,
          kind,
          label,
          // action,
        },
      });

      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
    }

    // Clear highlight after selection
    dispatch({ type: "HIGHLIGHT_CHANGED", payload: null });
    dispatch({ type: "POPOVER_CLOSED" });
    dispatch({ type: "QUERY_CHANGED", payload: "" });
  };

  const sendSubmitAction = useCallback(() => {
    if (mode === MDSComboboxMode.Passive) return;

    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, [inputRef, mode]);

  const handleArrowKeyPressed = useCallback(
    (direction: "up" | "down") => {
      if (!state.isOpen || state.data.length === 0) return;

      // Don't focus listbox - keep focus in input for continued typing
      // Instead, update highlighted item using reducer actions
      if (direction === "down") {
        dispatch({ type: "HIGHLIGHT_NEXT" });
      } else {
        dispatch({ type: "HIGHLIGHT_PREVIOUS" });
      }
    },
    [state.isOpen, state.data.length]
  );

  const handleEnterPressed = useCallback(async () => {
    // Context 1: If there's a highlighted item, select it (don't submit)
    if (state.highlightedItemId && state.data.length > 0) {
      const item = state.data.find(item => item.id === state.highlightedItemId);

      if (!item) {
        // Exit early - don't submit
        return;
      }

      const label = (await item.onClick?.()) ?? item.label;

      const kind = (() => {
        if (item.kind === "create-new-collection") {
          return MemCommonMentionKind.Collection;
        }
        return item.kind ?? MemCommonMentionKind.Note;
      })();

      if (state.popoverMentionMode === "explicit") {
        // Plus button mode - add to mentions via handler
        let mention: MDSComboboxMention;

        if (kind === MemCommonMentionKind.Collection) {
          mention = {
            kind: MemCommonMentionKind.Collection,
            isExternal: false,
            collection_id: item.id,
            collection_title: label,
          };
        } else {
          mention = {
            kind: MemCommonMentionKind.Note,
            isExternal: false,
            note_id: item.id,
            note_primary_label: label,
          };
        }

        onMentionAdd?.(mention);
      } else {
        // Implicit mode - insert into input text
        inputRef.current?.dispatchAction?.({
          kind: MemCommonRichTextInputActionKind.CloseMention,
          payload: {
            id: item?.id,
            kind,
            label,
          },
        });

        inputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }

      // Clear highlight after selection
      dispatch({ type: "HIGHLIGHT_CHANGED", payload: null });
      dispatch({ type: "POPOVER_CLOSED" });
      dispatch({ type: "QUERY_CHANGED", payload: "" });

      // Exit early after successful selection - don't submit form
      return;
    }

    // Context 2: No highlighted item - submit the form (only in implicit mode)
    if (state.popoverMentionMode === "implicit") {
      sendSubmitAction();
    }
  }, [
    state.highlightedItemId,
    state.data,
    state.popoverMentionMode,
    inputRef,
    sendSubmitAction,
    onMentionAdd,
  ]);

  const handleMentionRemove = useCallback(
    (mentionId: string) => {
      onMentionRemove?.(mentionId);
    },
    [onMentionRemove]
  );

  const handlePlusButtonClick = useCallback(() => {
    if (isDisabled) return;
    onLeftAddonButtonClick?.();
    handleMentionQuery("explicit");
  }, [isDisabled, handleMentionQuery, onLeftAddonButtonClick]);

  const handleSubmit = useCallback(
    async (payload: {
      mdxContent: string;
      htmlContent: string;
      markdownContent: string;
      tiptapJsonContent: JsonObject;
    }) => {
      const { mdxContent, htmlContent, tiptapJsonContent } = payload;

      // Parse mentions from the markdownContent and merge with external and implicit mentions
      const inputMentions = parseMentionsFromMarkdown(mdxContent);
      const allMentions = [...mentions, ...inputMentions];

      // Only submit if there's content
      if (mdxContent.trim()) {
        const success = await onSubmit?.({
          htmlContent,
          mdxContent,
          tiptapJsonContent,
          mentions: allMentions,
          implicitMentions,
        });

        if (mode === MDSComboboxMode.Passive) return;

        // Clear mentions via handler
        onMentionsClear?.();
        // TODO: the chat input appears to get cleared before we even get here

        if (success) {
          // Clear the input after successful submit
          inputRef.current?.dispatchAction?.({
            kind: MemCommonRichTextInputActionKind.SetEditorContent,
            payload: {
              content: "",
            },
          });
        } else {
          // restore the draft message
          inputRef.current?.dispatchAction?.({
            kind: MemCommonRichTextInputActionKind.SetEditorContent,
            payload: {
              content: htmlContent.replace(/(<p>\s*<\/p>\s*)$/i, ""), // the regex removes the "enter" line if the user submitted via the enter key
            },
          });
        }
      }
    },
    [mode, onSubmit, inputRef, mentions, implicitMentions, onMentionsClear]
  );

  const canSend = !isDisabled && !state.isEmpty;

  // Content display conditions for popover
  const isLoading = state.isLoading;
  const hasResults = state.data.length > 0;
  const showEmptyResults = state.data.length === 0 && state.hasSearched && !state.isLoading;

  // Show popover only when we have content to display
  // This prevents empty popover flashes during state transitions
  const shouldShowPopover = state.isOpen;

  return (
    <StyledCombobox ref={rootRef} data-mds-name="combobox" translate="no" className={className}>
      {label && <StyledLabel>{label}</StyledLabel>}

      <StyledInputContainer
        ref={inputContainerRef}
        variant={variant}
        hasLeftActionButton={true}
        aria-disabled={isDisabled}
        data-mds-name="combobox-input-container"
        onClick={handleContainerClick}
      >
        {/* Left addon - plus button */}
        <StyledLeftContainer>
          <StyledLeftAddonContainer>
            <MDSIconButton
              iconKind={MDSIconKind.Plus}
              variant={MDSIconButtonVariant.Default}
              onClick={handlePlusButtonClick}
              isDisabled={isDisabled}
              data-test-id="combobox-plus-button"
              rounded
              size={MDSIconButtonSize.Small}
            />
          </StyledLeftAddonContainer>

          {getSlot(children, MDSComboboxSlotName.LeftAddon)}
        </StyledLeftContainer>

        {/* Main input area */}
        <StyledInputArea>
          {!chatIsClosed && (
            <MentionChips
              isDisabled={isDisabled}
              mentions={mentions}
              onRemove={handleMentionRemove}
              onInputChange={emitInputHeightChange}
            />
          )}

          <StyledExpand>
            <CommonEditorInput
              dimensions={editorDimensions}
              onMentionQuerySet={query => handleMentionQuery("implicit", query)}
              onIsEmptyChange={handleIsEmptyChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onArrowKeyPressed={handleArrowKeyPressed}
              onEnterPressed={handleEnterPressed}
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              onInputChange={emitInputHeightChange}
              onContentModified={onContentModified}
              autoFocus={autoFocus}
              autoSubmit={mode !== MDSComboboxMode.Passive}
            />
          </StyledExpand>
        </StyledInputArea>

        {/* Right addon - action button or custom slot */}
        {mode !== MDSComboboxMode.Passive && (
          <StyledRightContainer $variant={variant}>
            <MDSIconButton
              iconKind={MDSIconKind.ArrowBrand}
              onClick={sendSubmitAction}
              isDisabled={!canSend}
              variant={MDSIconButtonVariant.Brand}
              rounded
              data-test-id="combobox-submit-button"
            />

            {/* Custom right addon slot */}
            {getSlot(children, MDSComboboxSlotName.RightAddon)}
          </StyledRightContainer>
        )}
      </StyledInputContainer>

      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}

      <StyledComboboxPopover
        isNonModal={state.popoverMentionMode === "implicit"}
        triggerRef={inputContainerRef}
        isOpen={shouldShowPopover}
        onOpenChange={isOpen => {
          // Don't close popover if we're programmatically focusing the listbox
          if (!isOpen && isProgrammaticFocusRef.current) {
            return;
          }

          // When popover closes, focus the input
          if (!isOpen) {
            inputRef.current?.dispatchAction({
              kind: MemCommonRichTextInputActionKind.FocusEditor,
              payload: null,
            });
          }

          dispatch({ type: isOpen ? "POPOVER_OPENED" : "POPOVER_CLOSED" });
        }}
        placement="bottom start"
        data-mds-name="combobox-popover"
        {...popoverProps}
      >
        <StyledPopoverContent ref={popoverContentRef}>
          {state.popoverMentionMode === "explicit" && (
            <StyledPopoverSearchContainer>
              <MDSTextField
                placeholder="Filter"
                value={state.query}
                onChange={query => handleMentionQuery("explicit", query)}
                onClearText={() => handleMentionQuery("explicit", "")}
                variant={MDSTextFieldVariant.Filter}
                showClearTextButton
                inputProps={{
                  autoFocus: !isMobile(),
                }}
              >
                <div slot={MDSTextFieldSlotName.LeftAddon}>
                  <MDSIcon kind={MDSIconKind.Search} />
                </div>
              </MDSTextField>
            </StyledPopoverSearchContainer>
          )}

          {hasResults && (
            <MDSSelectMenu
              onSelectionChange={handleSelection}
              selectionMode="single"
              shouldFocusWrap={true}
              selectedKeys={(() => {
                const selectedKeys: Set<string> = state.highlightedItemId
                  ? new Set([state.highlightedItemId])
                  : new Set();

                return selectedKeys;
              })()}
              data-mds-name="combobox-list-box"
              aria-label="Combobox"
              style={{ maxHeight: "75vh" }}
            >
              {state.data.map((item: MDSComboboxItem) => {
                if (item.isHeader) {
                  const label =
                    item.kind === MemCommonMentionKind.Note
                      ? "Mention a note"
                      : "Mention a collection";
                  return <MDSMenuItemHeader key={item.id} label={label} />;
                }

                const selectMenuItem: MDSSelectMenuItemProps = {
                  id: item.id,
                  label: item.label,
                  subtitle: getMenuItemSubtitle(item.kind as "collection" | "note", item.metadata),
                  icon: () => (
                    <MDSComboboxListItemIcon
                      kind={item.kind}
                      collectionId={item.metadata?.collectionId as string | undefined}
                    />
                  ),
                  onClick: item.onClick,
                };
                return <MDSSelectMenuItem key={item.id} {...selectMenuItem} />;
              })}
            </MDSSelectMenu>
          )}
          {showEmptyResults && (
            <StyledEmptyResult data-test-id="combobox-nothing-found">
              Nothing found
            </StyledEmptyResult>
          )}
          {isLoading && (
            <StyledEmptyResult data-test-id="combobox-loading">Loading...</StyledEmptyResult>
          )}
        </StyledPopoverContent>
      </StyledComboboxPopover>
    </StyledCombobox>
  );
};

// Function to parse mentions from markdownContent
const parseMentionsFromMarkdown = (markdownContent: string): MDSComboboxMention[] => {
  const mentions: MDSComboboxMention[] = [];
  const mentionRegex = /<a[^>]*data-type="mention-(note|collection)"[^>]*>/g;

  let match;
  while ((match = mentionRegex.exec(markdownContent)) !== null) {
    const anchorTag = match[0];

    // Extract data attributes
    const idMatch = anchorTag.match(/data-mention-id="([^"]+)"/);
    const kindMatch = anchorTag.match(/data-mention-kind="([^"]+)"/);
    const labelMatch = anchorTag.match(/data-mention-fallback-label="([^"]+)"/);

    if (idMatch && kindMatch && labelMatch) {
      const id = idMatch[1];
      const kind = kindMatch[1];
      const label = labelMatch[1];

      // Convert string kind to MemCommonMentionKind enum value
      if (kind === "collection") {
        mentions.push({
          kind: MemCommonMentionKind.Collection,
          collection_id: id,
          collection_title: label,
          isExternal: false,
        });
      } else if (kind === "note") {
        mentions.push({
          kind: MemCommonMentionKind.Note,
          note_id: id,
          note_primary_label: label,
          isExternal: false,
        });
      }
    }
  }

  return mentions;
};

const isMobile = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    navigator?.userAgent?.toLowerCase()
  );
};
